import React, { useState, useEffect } from "react";
import { 
  Card, 
  Layout, 
  Typography, 
  Button, 
  Divider, 
  Row, 
  Col, 
  Input, 
  Alert, 
  Space, 
  Select,
  Modal,
  message
} from "antd";
import { 
  PhoneOutlined, 
  MailOutlined,
  CheckOutlined,
  LockOutlined,
  UserOutlined,
  MessageOutlined
} from "@ant-design/icons";
import axios from "axios";
import { BACKENDURL } from "../../components/constants";
import axiosInstance from "../../axiosInstance";

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;
const { Option } = Select;

// Function to detect dev tools
const isDevToolsOpen = () => {
  const widthThreshold = 160;
  const heightThreshold = 160;
  
  return !!(window.outerWidth - window.innerWidth > widthThreshold || 
            window.outerHeight - window.innerHeight > heightThreshold);
};

// Input sanitization function
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove potentially dangerous content
  return input
    .replace(/<script[^>]*>([\S\s]*?)<\/script>/gmi, '')
    .replace(/<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gmi, '')
    .substring(0, 100); // Limit length
};

const UpgradePage = () => {
  const [selectedMethod, setSelectedMethod] = useState("mpesa");
  const [senderName, setSenderName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [securityAlert, setSecurityAlert] = useState(false);

  // Detect dev tools on mount and periodically
  useEffect(() => {
    const checkDevTools = () => {
      if (isDevToolsOpen()) {
        setSecurityAlert(true);
        console.error('Security violation detected');
      }
    };
    
    checkDevTools();
    const interval = setInterval(checkDevTools, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Obfuscated payment methods data (can be further encrypted in a real application)
  const paymentMethods = [
    {
      id: "mpesa",
      title: "M-PESA",
      icon: "/images/mpesa.png",
      description: "Pay using M-PESA",
      number: atob("MDc1NTM1NjI4NQ=="), // Base64 encoded
      name: atob("TVVHQUJPIFNURVBIIkFOTyBNQU5ZQU1B") // Base64 encoded
    },
    {
      id: "tigopesa",
      title: "Tigo Pesa",
      icon: "/images/yas.png",
      description: "Pay using Tigo Pesa",
      number: atob("MDcxNzIyNzAxOA=="),
      name: atob("SkFQSEVUIEtBSkFOQSBNR0lOSQ==")
    },
    {
      id: "halopesa",
      title: "HaloPesa",
      icon: "/images/halopesa.png",
      description: "Pay using HaloPesa",
      number: atob("MDYyNzc3MzAyMg=="),
      name: atob("TVVHQUJPIFNURVBIIkFOTyBNQU5ZQU1B")
    },
    {
      id: "crdb",
      title: "CRDB",
      icon: "/images/crdb.jpg",
      description: "Pay using CRDB Mobile Banking",
      number: atob("MDE1Mjc2Mzc2NzEwMA=="),
      name: atob("TVVHQUJPIFNURVBIIkFOTyBNQU5ZQU1B")
    }
  ];

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    // Sanitize inputs
    const sanitizedName = sanitizeInput(senderName);
    const sanitizedPhone = sanitizeInput(phoneNumber);
    
    if (!sanitizedName || !sanitizedPhone) {
      message.error("Please fill in all required fields with valid data");
      return;
    }

    setLoading(true);
    
    try {
      const response = await axiosInstance.post(`${BACKENDURL}/send-payments-notification`, {
        payment_method: selectedMethod,
        sender_name: sanitizedName,
        phone_number: sanitizedPhone
      });

      if (response.data.message) {
        message.success(response.data.message);
        setIsModalVisible(false);
        setIsConfirmationModalVisible(true);
        
        // Reset form
        setSenderName("");
        setPhoneNumber("");
      }
    } catch (error) {
      console.error('Payment confirmation error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to confirm payment';
      message.error(sanitizeInput(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleConfirmationOk = () => {
    setIsConfirmationModalVisible(false);
  };

  const handleConfirmationCancel = () => {
    setIsConfirmationModalVisible(false);
  };

  const selectedPayment = paymentMethods.find(method => method.id === selectedMethod);

  return (
    <Layout style={{ minHeight: "100vh", background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)", padding: "20px" }}>
      <Content style={{ maxWidth: "800px", margin: "80px auto 20px" }}>
        {securityAlert && (
          <Alert
            message="Security Alert"
            description="Inspection tools detected. For security reasons, some functionality may be limited."
            type="warning"
            showIcon
            closable
            style={{ marginBottom: "20px" }}
          />
        )}
        
        <Card 
          style={{ 
            borderRadius: "16px", 
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
            position: "relative"
          }}
          bodyStyle={{ padding: "0" }}
        >
          {/* Anti-tampering overlay */}
          <div 
            style={{ 
              position: "absolute", 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              zIndex: 9999,
              display: securityAlert ? "block" : "none",
              cursor: "not-allowed"
            }}
            onClick={(e) => {
              e.stopPropagation();
              message.warning("Security restrictions in place");
            }}
          />
          
          {/* Header */}
          <div style={{ 
            background: "linear-gradient(90deg, #4b6cb7 0%, #182848 100%)", 
            color: "white", 
            padding: "25px", 
            textAlign: "center" 
          }}>
            <Title level={2} style={{ color: "white", marginBottom: "10px", fontSize: "24px" }}>
              <LockOutlined /> Payment Service
            </Title>
            <Text style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "16px" }}>
              Select your payment method and contact us to confirm your payment
            </Text>
          </div>

          {/* Important Notice */}
          <Alert
            message={
              <div>
                <Title level={5} style={{ marginBottom: "8px" }}>
                  <CheckOutlined /> Important Notice
                </Title>
                <Paragraph style={{ marginBottom: "0" }}>
                  After making a payment, please contact us through our support numbers to confirm your payment. 
                  Your account will be activated after verification.
                </Paragraph>
              </div>
            }
            type="info"
            showIcon
            style={{ 
              margin: "20px", 
              borderRadius: "8px",
              background: "#f3f8ff",
              border: "1px solid #91d5ff"
            }}
          />

          <div style={{ padding: "20px" }}>
            {/* Payment Methods */}
            <Title level={4} style={{ marginBottom: "15px", color: "#2c3e50" }}>
              Select Payment Method
            </Title>
            
            <Row gutter={[16, 16]} style={{ marginBottom: "25px" }}>
              {paymentMethods.map(method => (
                <Col xs={24} md={12} key={method.id}>
                  <Card
                    hoverable
                    style={{
                      border: selectedMethod === method.id ? "2px solid #4b6cb7" : "1px solid #e6e6e6",
                      background: selectedMethod === method.id ? "#f3f8ff" : "white",
                      borderRadius: "12px",
                      height: "100%",
                      transition: "all 0.3s",
                      cursor: securityAlert ? "not-allowed" : "pointer"
                    }}
                    bodyStyle={{ padding: "15px", textAlign: "center" }}
                    onClick={() => !securityAlert && setSelectedMethod(method.id)}
                  >
                    <div style={{ height: "40px", marginBottom: "10px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                      <img 
                        src={method.icon} 
                        alt={method.title}
                        style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/150x40?text=" + method.title;
                        }}
                      />
                    </div>
                    <Title level={5} style={{ marginBottom: "8px", color: "#2c3e50", fontSize: "16px" }}>
                      {method.title}
                    </Title>
                    <Text style={{ color: "#7f8c8d", fontSize: "14px" }}>{method.description}</Text>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Payment Instructions */}
            <Divider />
            
            <Title level={4} style={{ marginBottom: "15px", color: "#2c3e50" }}>
              Payment Instructions
            </Title>
            
            {selectedPayment && (
              <Card style={{ background: "#f9f9f9", marginBottom: "20px", borderRadius: "8px" }}>
                <Paragraph strong>
                  {selectedPayment.title} Number: <Text style={{ color: "#4b6cb7" }}>{selectedPayment.number}</Text>
                </Paragraph>
                <Paragraph strong>
                  Name: <Text style={{ color: "#4b6cb7" }}>{selectedPayment.name}</Text>
                </Paragraph>
                <Paragraph>
                  Send your payment amount to the number above, then click the "Confirm Payment" button to fill in your payment details.
                </Paragraph>
              </Card>
            )}

            {/* Action Buttons */}
            <div style={{ marginTop: "25px", display: "flex", justifyContent: "space-between" }}>
              <Button 
                type="primary" 
                size="large" 
                style={{ background: "#4b6cb7", borderColor: "#4b6cb7" }}
                onClick={securityAlert ? () => message.warning("Security restrictions in place") : showModal}
                disabled={securityAlert}
              >
                Confirm Payment
              </Button>
            </div>

            {/* Support Contact */}
            <Divider />
            
            <div style={{ textAlign: "center" }}>
              <Title level={5} style={{ color: "#7f8c8d", marginBottom: "15px" }}>
                Need payment assistance? Contact us through:
              </Title>
              
              <Space direction="vertical" size="middle" style={{ width: "100%", justifyContent: "center" }}>
                <div>
                  <PhoneOutlined style={{ color: "#4b6cb7", marginRight: "8px" }} />
                  <Text>+255 782 913 983 | 0717 227 018</Text>
                </div>
                <div>
                  <MessageOutlined style={{ color: "#4b6cb7", marginRight: "8px" }} />
                  <Text>+255 782 913 983 (WhatsApp)</Text>
                </div>
                <div>
                  <MailOutlined style={{ color: "#4b6cb7", marginRight: "8px" }} />
                  <Text>support@sellsmart.co.tz</Text>
                </div>
              </Space>
            </div>
          </div>
        </Card>

        {/* Payment Confirmation Modal */}
        <Modal
          title="Confirm Your Payment"
          visible={isModalVisible}
          onOk={handleOk}
          onCancel={handleCancel}
          confirmLoading={loading}
          footer={[
            <Button key="back" onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>,
            <Button 
              key="submit" 
              type="primary" 
              onClick={handleOk}
              loading={loading}
              style={{ background: "#4b6cb7", borderColor: "#4b6cb7" }}
            >
              Submit
            </Button>,
          ]}
        >
          <Paragraph>
            Please fill in the following information according to the payment you made. We will confirm your payment soon!.
          </Paragraph>
          
          <div style={{ marginBottom: "15px" }}>
            <Text strong>Payment Method</Text>
            <Select
              style={{ width: "100%", marginTop: "5px" }}
              value={selectedMethod}
              onChange={setSelectedMethod}
              disabled={loading}
            >
              {paymentMethods.map(method => (
                <Option key={method.id} value={method.id}>{method.title}</Option>
              ))}
            </Select>
          </div>
          
          <div style={{ marginBottom: "15px" }}>
            <Text strong>Your Name (as it appears on the payment)</Text>
            <Input
              placeholder="Your name"
              value={senderName}
              onChange={(e) => setSenderName(sanitizeInput(e.target.value))}
              style={{ marginTop: "5px" }}
              prefix={<UserOutlined />}
              disabled={loading}
              maxLength={100}
            />
          </div>
          
          <div style={{ marginBottom: "15px" }}>
            <Text strong>Your Phone Number</Text>
            <Input
              placeholder="Phone number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(sanitizeInput(e.target.value))}
              style={{ marginTop: "5px" }}
              prefix={<PhoneOutlined />}
              disabled={loading}
              maxLength={20}
            />
          </div>
        </Modal>

        {/* Confirmation Success Modal */}
        <Modal
          title="Payment Received"
          visible={isConfirmationModalVisible}
          onOk={handleConfirmationOk}
          onCancel={handleConfirmationCancel}
          footer={[
            <Button 
              key="ok" 
              type="primary" 
              onClick={handleConfirmationOk}
              style={{ background: "#4b6cb7", borderColor: "#4b6cb7" }}
            >
              OK
            </Button>,
          ]}
        >
          <div style={{ textAlign: "center" }}>
            <CheckOutlined style={{ fontSize: "48px", color: "#52c41a", marginBottom: "15px" }} />
            <Title level={4} style={{ color: "#2c3e50" }}>
              Thank you for confirming your payment!
            </Title>
            <Paragraph>
              Your information has been received successfully. We will confirm your payment via SMS or email soon.
            </Paragraph>
            <Paragraph>
              If you have any questions, contact us through the support numbers listed below.
            </Paragraph>
          </div>
        </Modal>
      </Content>
    </Layout>
  );
};

export default UpgradePage;